// Temporary diagnostic for the "tab bar stops responding after a while" bug.
// Hooks pointer events at the WINDOW level — no TabBar.js modification.
// Logs flow through DebugScreen because it captures console.warn.
//
// Usage:
//   import { installTabBarObserver } from "./tabBarObserver";
//   useEffect(() => installTabBarObserver(), []);
//
// To inspect: open DebugScreen and read the recent "[tabprobe]" warn lines.
// Disable when fix is verified.

const RING = [];
const MAX = 40;

function push(entry) {
  RING.push(entry);
  if (RING.length > MAX) RING.shift();
  // Surface to console.warn so DebugScreen captures it
  // eslint-disable-next-line no-console
  console.warn("[tabprobe]", entry);
}

function describeTopElements(x, y) {
  try {
    const els = document.elementsFromPoint(x, y);
    return els.slice(0, 4).map(el => {
      const tag = el.tagName?.toLowerCase() || "?";
      const id = el.id ? `#${el.id}` : "";
      const cls = (el.className && typeof el.className === "string")
        ? "." + el.className.split(/\s+/).slice(0, 2).join(".") : "";
      const dt = el.getAttribute?.("data-tab-id") ? `[tab=${el.getAttribute("data-tab-id")}]` : "";
      return `${tag}${id}${cls}${dt}`.slice(0, 60);
    }).join(" › ");
  } catch (e) { return `elementsFromPoint-err:${e.message}`; }
}

function inBottomBarZone(y) {
  // Bar sits at the bottom ~100px (60-72 bar + safe area). Anything below
  // (innerHeight - 110) is "inside" the bar's interactive zone.
  return y > (window.innerHeight - 110);
}

let installed = false;
let pendingDown = null;

export function installTabBarObserver() {
  if (installed || typeof window === "undefined") return () => {};
  installed = true;

  const onDown = (e) => {
    if (!inBottomBarZone(e.clientY)) return;
    const stack = describeTopElements(e.clientX, e.clientY);
    pendingDown = { ts: Date.now(), x: Math.round(e.clientX), y: Math.round(e.clientY), pid: e.pointerId, ptype: e.pointerType };
    push({
      e: "DOWN",
      pid: e.pointerId,
      x: Math.round(e.clientX),
      y: Math.round(e.clientY),
      tag: e.target?.tagName,
      tabId: e.target?.closest?.("[data-tab-id]")?.getAttribute?.("data-tab-id") || null,
      stack,
      iw: window.innerWidth,
      ih: window.innerHeight,
    });
  };

  const onUp = (e) => {
    if (!inBottomBarZone(e.clientY) && !pendingDown) return;
    const dt = pendingDown ? Date.now() - pendingDown.ts : null;
    push({
      e: "UP",
      pid: e.pointerId,
      x: Math.round(e.clientX),
      y: Math.round(e.clientY),
      tag: e.target?.tagName,
      tabId: e.target?.closest?.("[data-tab-id]")?.getAttribute?.("data-tab-id") || null,
      dtMs: dt,
    });
    pendingDown = null;
  };

  const onCancel = (e) => {
    push({
      e: "CANCEL",
      pid: e.pointerId,
      ptype: e.pointerType,
      tag: e.target?.tagName,
    });
    pendingDown = null;
  };

  // Capture phase — fires BEFORE any element listener, so we see
  // everything iOS sends to the page, even if a child stops propagation.
  window.addEventListener("pointerdown", onDown, true);
  window.addEventListener("pointerup", onUp, true);
  window.addEventListener("pointercancel", onCancel, true);

  // Expose buffer for inspection
  window.__tabprobe = RING;

  return () => {
    installed = false;
    window.removeEventListener("pointerdown", onDown, true);
    window.removeEventListener("pointerup", onUp, true);
    window.removeEventListener("pointercancel", onCancel, true);
  };
}
